import React from "react";
import {
  Box,
  Button,
  Container,
  Flex,
  Heading,
  Link,
  SimpleGrid,
  Text,
} from "@chakra-ui/react";

import resources from "./resources";

const categories = [
  {
    title: "Implementation Briefs",
    description:
      "Practical outputs that capture what we are learning about translating health evidence into working pathways.",
    types: ["Implementation Brief"],
  },
  {
    title: "Concept Notes",
    description:
      "Early frameworks and follow-on ideas developed from Serena Space Institute discussions and research.",
    types: ["Concept Note"],
  },
  {
    title: "Seminar Materials",
    description:
      "Materials connected to Institute seminars and scientific exchange.",
    types: ["Seminar Material"],
  },
  {
    title: "Research & Technical",
    description:
      "Research, technical and scientific materials emerging from Serena's work.",
    types: ["Research", "Technical"],
  },
];

function ResourceCard({ resource }) {
  return (
    <Box
      border="1px solid"
      borderColor="blackAlpha.150"
      bg="white"
      p={{ base: 6, md: 7 }}
      borderRadius="2xl"
      display="flex"
      flexDirection="column"
      height="100%"
    >
      <Flex
        justify="space-between"
        align="flex-start"
        gap={4}
        mb={5}
      >
        <Text
          fontSize="xs"
          fontWeight="700"
          letterSpacing="0.1em"
          textTransform="uppercase"
          color="#087F73"
        >
          {resource.type}
        </Text>

        <Text
          fontSize="xs"
          color="blackAlpha.500"
          whiteSpace="nowrap"
        >
          {resource.date}
        </Text>
      </Flex>

      <Heading
        as="h3"
        fontFamily="'Poppins', sans-serif"
        fontSize={{ base: "xl", md: "2xl" }}
        lineHeight="1.2"
        letterSpacing="-0.025em"
        color="#17211F"
        mb={4}
      >
        {resource.title}
      </Heading>

      <Text
        fontSize="sm"
        lineHeight="1.7"
        color="blackAlpha.700"
        mb={7}
      >
        {resource.description}
      </Text>

      <Flex gap={3} mt="auto" flexWrap="wrap">
        {resource.url && !resource.file ? (
          <Link
            href={resource.url}
            color="#087F73"
            fontSize="sm"
            fontWeight="600"
            textDecoration="underline"
            _hover={{ color: "#06695F" }}
          >
            Read summary
          </Link>
        ) : resource.file ? (
          <>
            <Button
              as="a"
              href={resource.file}
              target="_blank"
              rel="noopener noreferrer"
              bg="#087F73"
              color="white"
              borderRadius="full"
              px={5}
              size="sm"
              _hover={{
                bg: "#06695F",
              }}
            >
              View resource
            </Button>

            <Button
              as="a"
              href={resource.file}
              download
              variant="outline"
              borderRadius="full"
              px={5}
              size="sm"
              borderColor="blackAlpha.200"
              color="#17211F"
              _hover={{
                bg: "#F3F0E8",
              }}
            >
              Download
            </Button>
          </>
        ) : null}
      </Flex>
    </Box>
  );
}

function ResourcesPage() {
  return (
    <Box bg="#F9FAF8">
      {/* HERO */}
      <Box
        bg="#17211F"
        color="white"
        py={{ base: 20, md: 28 }}
      >
        <Container
          maxW="1400px"
          px={{ base: 5, md: 8, lg: 12 }}
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.14em"
            textTransform="uppercase"
            color="whiteAlpha.600"
            mb={5}
          >
            Knowledge & Resources
          </Text>

          <Heading
            as="h1"
            fontFamily="'Poppins', sans-serif"
            fontSize={{
              base: "4xl",
              md: "5xl",
              lg: "6xl",
            }}
            lineHeight="1.02"
            letterSpacing="-0.045em"
            maxW="850px"
            mb={7}
          >
            From discussion to usable knowledge.
          </Heading>

          <Text
            maxW="720px"
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.75"
            color="whiteAlpha.700"
          >
            Explore briefs, concept notes, seminar materials and other
            resources emerging from Serena Space's scientific and
            translational work.
          </Text>
        </Container>
      </Box>

      {/* RESOURCE LIBRARY */}
      <Container
        maxW="1400px"
        px={{ base: 5, md: 8, lg: 12 }}
        py={{ base: 16, md: 24 }}
      >
        <Box mb={{ base: 12, md: 16 }}>
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="#087F73"
            mb={4}
          >
            Resource library
          </Text>

          <Heading
            as="h2"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "3xl", md: "4xl" }}
            letterSpacing="-0.035em"
            color="#17211F"
            maxW="700px"
          >
            Knowledge that stays accessible.
          </Heading>

          <Text
            maxW="700px"
            mt={5}
            fontSize={{ base: "md", md: "lg" }}
            lineHeight="1.75"
            color="blackAlpha.650"
          >
            We publish selected outputs from our research, scientific
            exchange and implementation work so that ideas can move beyond
            the room in which they were developed.
          </Text>
        </Box>

        {categories.map((category) => {
          const categoryResources = resources.filter((resource) =>
            category.types.includes(resource.type)
          );

          if (!categoryResources.length) {
            return null;
          }

          return (
            <Box key={category.title} mb={{ base: 14, md: 20 }}>
              <Box mb={7}>
                <Heading
                  as="h3"
                  fontFamily="'Poppins', sans-serif"
                  fontSize={{ base: "2xl", md: "3xl" }}
                  letterSpacing="-0.03em"
                  color="#17211F"
                >
                  {category.title}
                </Heading>

                <Text
                  maxW="650px"
                  mt={3}
                  fontSize="sm"
                  lineHeight="1.7"
                  color="blackAlpha.600"
                >
                  {category.description}
                </Text>
              </Box>

              <SimpleGrid
                columns={{ base: 1, md: 2 }}
                spacing={6}
              >
                {categoryResources.map((resource) => (
                  <ResourceCard
                    key={resource.id}
                    resource={resource}
                  />
                ))}
              </SimpleGrid>
            </Box>
          );
        })}

        {/* EMPTY / FUTURE LIBRARY NOTE */}
        <Box
          mt={8}
          p={{ base: 7, md: 10 }}
          bg="#F3F0E8"
          borderRadius="2xl"
        >
          <Text
            fontSize="xs"
            fontWeight="700"
            letterSpacing="0.12em"
            textTransform="uppercase"
            color="#087F73"
            mb={4}
          >
            More to come
          </Text>

          <Heading
            as="h3"
            fontFamily="'Poppins', sans-serif"
            fontSize={{ base: "xl", md: "2xl" }}
            letterSpacing="-0.025em"
            color="#17211F"
            mb={3}
          >
            This library will grow with the work.
          </Heading>

          <Text
            maxW="700px"
            fontSize="sm"
            lineHeight="1.7"
            color="blackAlpha.650"
          >
            As Serena's research, seminars and implementation projects
            develop, this space will become a growing record of the
            evidence, questions and practical knowledge emerging from the
            work.
          </Text>
        </Box>
      </Container>
    </Box>
  );
}

export default ResourcesPage;